import {
  ReactNode,
  useRef,
  useCallback,
  MutableRefObject,
  FocusEvent,
  KeyboardEvent,
  RefObject,
} from 'react';
import clsx from 'clsx';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { SidebarOpenStateProvider } from '@backstage/core-components';
import {
  handleActivationKeyDown,
  handleEscapeKeyDown,
  FOCUSABLE_SELECTOR,
  deferFocus,
  getNextIndex,
} from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { POPOUT_VERTICAL_OFFSET } from './constants';
import { useSharedStyles, useCollapsedStyles } from './styles';

export type CollapsedSidebarGroupProps = {
  icon: ReactNode;
  label: ReactNode;
  groupId: string;
  children: ReactNode;
  hasActiveChild: boolean;
  isHovered: boolean;
  isMainItemHovered: boolean;
  isSubmenuActive: boolean;
  onGroupHover: (groupId: string | null) => void;
  handleMainItemMouseEnter: () => void;
  handleMainItemMouseLeave: () => void;
  handlePopoutMouseEnter: () => void;
  handlePopoutMouseLeave: () => void;
  handleMainItemFocus: () => void;
  handleMainItemBlur: (e: FocusEvent) => void;
  handleActivateSubmenu: () => void;
  handleDeactivateSubmenu: () => void;
  onGroupFocus: (groupId: string | null) => void;
  mainItemRef: RefObject<HTMLElement>;
  popoutRef: RefObject<HTMLDivElement>;
};

export const CollapsedSidebarGroup = ({
  icon,
  label,
  groupId,
  children,
  hasActiveChild,
  isHovered,
  isMainItemHovered,
  isSubmenuActive,
  onGroupHover,
  handleMainItemMouseEnter,
  handleMainItemMouseLeave,
  handlePopoutMouseEnter,
  handlePopoutMouseLeave,
  handleMainItemFocus,
  handleMainItemBlur,
  handleActivateSubmenu,
  handleDeactivateSubmenu,
  onGroupFocus,
  mainItemRef,
  popoutRef,
}: CollapsedSidebarGroupProps) => {
  const sharedClasses = useSharedStyles();
  const collapsedClasses = useCollapsedStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const listItemRef = useRef<HTMLElement | null>(
    null,
  ) as MutableRefObject<HTMLElement | null>;

  // Generate a stable ID for aria-labelledby using groupId
  const groupLabelId = `sidebar-group-${groupId}`;

  // Handler to activate the currently focused item in submenu (for Enter/Space)
  const handleActivateFocusedItem = useCallback(() => {
    const activeEl = document.activeElement as HTMLElement;
    if (popoutRef.current?.contains(activeEl) && activeEl) {
      // If it's a link, click it; otherwise try to trigger click event
      if (activeEl.tagName === 'A' && activeEl instanceof HTMLAnchorElement) {
        activeEl.click();
      } else if (activeEl.onclick || activeEl.getAttribute('onclick')) {
        activeEl.click();
      }
      // Close submenu, clear focused state (so popout stays hidden), but keep focus on main item
      handleDeactivateSubmenu();
      onGroupFocus(null);
      // Ensure main item still has focus (for accessibility)
      deferFocus(mainItemRef.current);
    }
  }, [popoutRef, handleDeactivateSubmenu, onGroupFocus, mainItemRef]);

  // Handle Tab key on main item - should skip to next main item, not enter submenu
  const handleMainItemKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Tab should move to next main item, not activate submenu
      if (e.key === 'Tab') {
        // Let Tab work normally - it will move focus to next focusable element
        return;
      }
      // ArrowRight activates submenu (same as Enter/Space)
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleActivateSubmenu();
        return;
      }
      // ArrowDown/Up: navigate between main items (like Tab/Shift+Tab)
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Find the sidebar content container that holds all main items
        // Note: Uses class-based selector which may be fragile if CSS class names change
        const sidebarContent =
          anchorRef.current?.closest(
            '[class*="content"], [class*="sidebar"]',
          ) || document.body;
        // Find all focusable main sidebar items, excluding submenu items
        const allFocusable = Array.from(
          sidebarContent.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter(
          item =>
            // Exclude items inside popouts/menus (submenu items)
            item.closest('[role="menu"]') === null &&
            // Exclude items inside collapsed popout containers
            item.closest('[class*="popout"]') === null &&
            // Exclude items inside nested lists (submenu items)
            item.closest('.MuiCollapse-root') === null &&
            // Make sure it's visible (not hidden)
            item.offsetParent !== null,
        );

        if (allFocusable.length === 0) return;

        const currentIndex = allFocusable.findIndex(
          item => item === listItemRef.current,
        );
        if (currentIndex === -1) {
          // If current item not found, focus first/last based on direction
          const fallbackIndex =
            e.key === 'ArrowDown' ? 0 : allFocusable.length - 1;
          allFocusable[fallbackIndex]?.focus();
          return;
        }

        const direction = e.key === 'ArrowDown' ? 'next' : 'prev';
        const nextIndex = getNextIndex(
          currentIndex,
          allFocusable.length,
          direction,
        );
        allFocusable[nextIndex]?.focus();
        return;
      }
      // Enter/Space activates submenu
      handleActivationKeyDown(handleActivateSubmenu)(e);
    },
    [handleActivateSubmenu],
  );

  // Focus trap: handle Tab key cycling within submenu
  const handlePopoutKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isSubmenuActive) return;

      // ArrowLeft deactivates submenu and returns focus to main item (same as Escape)
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleDeactivateSubmenu();
        return;
      }

      // Get all focusable elements in submenu
      const focusableElements = Array.from(
        popoutRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ||
          [],
      );

      if (focusableElements.length === 0) {
        // No focusable elements, prevent navigation
        if (e.key === 'Tab' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
        }
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;
      const isFocusInPopout = popoutRef.current?.contains(activeElement);

      // ArrowDown/Up: navigate between submenu items (like Tab/Shift+Tab)
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();

        // If focus is not in popout, focus the first element
        if (!isFocusInPopout) {
          firstElement.focus();
          return;
        }

        const currentIndex = focusableElements.findIndex(
          el => el === activeElement,
        );
        if (currentIndex === -1) {
          firstElement.focus();
          return;
        }

        const direction = e.key === 'ArrowDown' ? 'next' : 'prev';
        const nextIndex = getNextIndex(
          currentIndex,
          focusableElements.length,
          direction,
        );
        focusableElements[nextIndex]?.focus();
        return;
      }

      // Enter/Space: activate the focused link/item
      handleActivationKeyDown(handleActivateFocusedItem)(e);

      if (e.key === 'Tab') {
        // If focus is not in popout, focus the first element
        if (!isFocusInPopout) {
          e.preventDefault();
          firstElement.focus();
          return;
        }

        if (e.shiftKey) {
          // Shift+Tab: if on first element, wrap to last
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, wrap to first
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [
      isSubmenuActive,
      popoutRef,
      handleDeactivateSubmenu,
      handleActivateFocusedItem,
    ],
  );

  // When collapsed, show icon with hover popout submenu
  // Apply active style (left border indicator) if any child route is active
  // Disable default hover effect since CSS-driven overlay provides it
  const headerClassName = clsx(
    sharedClasses.header,
    hasActiveChild && sharedClasses.headerActive,
  );

  // Popout should be visible when hovered or submenu is active (not on focus alone)
  const isPopoutOpen = isHovered || isSubmenuActive;

  return (
    <div
      ref={anchorRef}
      className={clsx(
        collapsedClasses.collapsedItem,
        isMainItemHovered && collapsedClasses.collapsedItemHovered,
      )}
      onMouseEnter={handleMainItemMouseEnter}
      onMouseLeave={handleMainItemMouseLeave}
    >
      <ListItem
        ref={node => {
          listItemRef.current = node;
          if (mainItemRef && 'current' in mainItemRef) {
            (mainItemRef as MutableRefObject<HTMLElement | null>).current =
              node;
          }
        }}
        button
        className={headerClassName}
        tabIndex={0}
        role="button"
        aria-expanded={isPopoutOpen}
        aria-haspopup="true"
        onFocus={handleMainItemFocus}
        onBlur={handleMainItemBlur}
        onKeyDown={handleMainItemKeyDown}
      >
        <ListItemIcon className={sharedClasses.headerIcon}>{icon}</ListItemIcon>
        <ChevronRightIcon
          className={clsx(
            collapsedClasses.collapsedCaret,
            hasActiveChild && collapsedClasses.collapsedCaretActive,
          )}
          data-testid="ChevronRightIcon"
        />
      </ListItem>

      <Popper
        open={isPopoutOpen && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        placement="right-start"
        style={{ zIndex: 1300 }} // zIndex must be inline for Popper portal positioning
        keepMounted
        modifiers={{
          offset: {
            enabled: true,
            // Offset upward so first submenu item aligns with the main menu item,
            // making hover navigation easier
            offset: `${-POPOUT_VERTICAL_OFFSET}, 0`,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        <div
          ref={popoutRef}
          className={collapsedClasses.popoutContainer}
          onKeyDown={e => {
            handlePopoutKeyDown(e);
            handleEscapeKeyDown(handleDeactivateSubmenu)(e);
          }}
          role="menu"
          aria-labelledby={groupLabelId}
        >
          {/* Invisible bridge for hover continuity between popout and main item */}
          <div
            className={collapsedClasses.hoverBridge}
            onMouseEnter={handlePopoutMouseEnter}
            onMouseLeave={handlePopoutMouseLeave}
          />
          <Paper
            className={collapsedClasses.popoutPaper}
            onMouseEnter={handlePopoutMouseEnter}
            onMouseLeave={handlePopoutMouseLeave}
          >
            <Typography
              id={groupLabelId}
              variant="subtitle2"
              className={collapsedClasses.popoutHeader}
            >
              {label}
            </Typography>
            <SidebarOpenStateProvider
              value={{ isOpen: true, setOpen: () => {} }}
            >
              <List
                component="nav"
                className={collapsedClasses.popoutList}
                onClick={() => {
                  onGroupHover(null);
                  // Close submenu, clear focused state (so popout stays hidden), but keep focus on main item
                  if (isSubmenuActive) {
                    handleDeactivateSubmenu();
                    onGroupFocus(null);
                    // Ensure main item still has focus (for accessibility)
                    deferFocus(mainItemRef.current);
                  }
                }}
              >
                {children}
              </List>
            </SidebarOpenStateProvider>
          </Paper>
        </div>
      </Popper>
    </div>
  );
};
